// src/components/admin/shared/ColorPicker.js
import React, { useState, useRef, useEffect } from "react";

/**
 * A simple color picker component with optional alpha channel support
 */
const ColorPicker = ({ color = "#000000", onChange, allowAlpha = false }) => {
  const [showPicker, setShowPicker] = useState(false);
  const [currentColor, setCurrentColor] = useState(color);
  const pickerRef = useRef(null);

  // Handle clicks outside the picker to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update internal state when color prop changes
  useEffect(() => {
    setCurrentColor(color);
  }, [color]);

  // Handle color change
  const handleColorChange = (e) => {
    const newColor = e.target.value;
    setCurrentColor(newColor);
    onChange(newColor);
  };

  // For colors with alpha channel, we need to handle rgba
  const isRgba = currentColor.startsWith("rgba");
  const hasAlpha =
    isRgba || (currentColor.startsWith("#") && currentColor.length === 9);

  // Convert rgba to hex if needed for the color input
  const getInputColor = () => {
    if (isRgba && !allowAlpha) {
      // Extract rgb values
      const match = currentColor.match(
        /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([.\d]+))?\)/
      );
      if (match) {
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);
        return `#${r.toString(16).padStart(2, "0")}${g
          .toString(16)
          .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
      }
    }
    return currentColor;
  };

  return (
    <div className="relative" ref={pickerRef}>
      <div
        className="flex items-center border border-gray-300 rounded p-2 cursor-pointer"
        onClick={() => setShowPicker(!showPicker)}
      >
        <div
          className="w-8 h-6 rounded border border-gray-400"
          style={{ backgroundColor: currentColor }}
        ></div>
        <input
          type="text"
          value={currentColor}
          onChange={(e) => {
            setCurrentColor(e.target.value);
            onChange(e.target.value);
          }}
          className="ml-2 flex-1 outline-none text-sm"
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {showPicker && (
        <div className="absolute z-50 mt-2 p-3 bg-white border border-gray-300 rounded shadow-lg">
          <input
            type="color"
            value={getInputColor()}
            onChange={handleColorChange}
            className="w-full h-10 cursor-pointer"
          />

          {allowAlpha && (
            <div className="mt-2">
              <label className="block text-sm text-gray-700 mb-1">
                Opacity
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={
                  hasAlpha
                    ? parseFloat(
                        currentColor.match(
                          /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([.\d]+))?\)/
                        )[4]
                      )
                    : 1
                }
                onChange={(e) => {
                  const alpha = parseFloat(e.target.value);
                  // Extract RGB values or use defaults
                  let r = 0,
                    g = 0,
                    b = 0;

                  if (isRgba) {
                    const match = currentColor.match(
                      /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([.\d]+))?\)/
                    );
                    if (match) {
                      r = parseInt(match[1]);
                      g = parseInt(match[2]);
                      b = parseInt(match[3]);
                    }
                  } else if (currentColor.startsWith("#")) {
                    // Convert hex to rgb
                    let hex = currentColor.substring(1);
                    if (hex.length === 3) {
                      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
                    }
                    r = parseInt(hex.substring(0, 2), 16);
                    g = parseInt(hex.substring(2, 4), 16);
                    b = parseInt(hex.substring(4, 6), 16);
                  }

                  const newColor = `rgba(${r}, ${g}, ${b}, ${alpha})`;
                  setCurrentColor(newColor);
                  onChange(newColor);
                }}
                className="w-full"
              />
            </div>
          )}

          <div className="flex justify-between mt-3">
            <button
              onClick={() => setShowPicker(false)}
              className="px-3 py-1 bg-gray-200 text-gray-800 rounded text-sm hover:bg-gray-300"
            >
              Close
            </button>
            <button
              onClick={() => {
                onChange(currentColor);
                setShowPicker(false);
              }}
              className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPicker;
